import React from 'react';
import Accordion, { HeaderPrimaryItem } from '../shared/Accordion';
import { ResourceStrings } from '../../ResourceStrings';
import { SearchResult } from '../../SearchResults';
import styled from 'styled-components/macro';
import { Icon } from '@fluentui/react/lib/Icon';
import { Fetchable } from '../../store/appState';
import { App, SignInState } from '../../app';
import Solutions from './Submission/Solutions';
import Comments from './Submission/Comments';
import { CommentType } from '../../PublicFeedback';
import { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { forMinWidth } from '../../theme/breakpoints';
import { PostType, RootPost } from '../../RootPost';
import HiddenTextInfo from './Details/HiddenTextInfo';
import HtmlPreview from '../shared/HtmlPreview';
import { SpaceId } from '../../config/spaces/spaceIds';
import { DeflectInfo } from './Submission/SubmitPostView';

export interface PostDetailProps {
    deflectInfo: DeflectInfo;
    post: SearchResult;
    postV3?: Fetchable<RootPost>;
    signInState?: SignInState;
    userId: string;
    hideVotes?: boolean;
    onJoinDiscussion?: (post: RootPost) => void;
    onUpvote?: (post: RootPost) => void;
}

export default function PostDetail(
    { deflectInfo, post, hideVotes, userId, onJoinDiscussion, onUpvote, postV3, signInState }: PostDetailProps
): JSX.Element {
    let reportDate = "";
    if (post?.createdDateUtc) {
        reportDate = App.ParseDateTimeToLocal(post.createdDateUtc);
    }

    const getVoteTooltipText = (post: SearchResult, signInState: SignInState): string => {
        if (post.iCreatedThis) {
            return ResourceStrings.youCreatedIt();
        }

        if (post.hasVoted) {
            return ResourceStrings.alreadyVoted();
        }

        if (signInState !== SignInState.Success) {
            return ResourceStrings.authNeeded_Vote();
        }

        if (post.cantVote) {
            return ResourceStrings.cantVote();
        }

        return ResourceStrings.vote();
    }

    // // const voteClick = (post: SearchResult, signInState: SignInState, voteState: OperationState): void => {
    // //     App.telemetry.postBusinessEvent("voteproblem-clicked", {
    // //         feedbackItemUrl: this.props.state.selectedItem ? this.props.state.selectedItem.communityUrl : "",
    // //     });

    // //     if (this.props.state.signInState !== SignInState.Success) {
    // //         App.telemetry.postUserTask("VoteClickNotLoggedIn", TelemetryResult.Success);
    // //         App.showMessageBox(ResourceStrings.authNeeded_Vote());
    // //         return;
    // //     }

    // //     if (!this.props.item.hasVoted &&
    // //         (this.props.item.cantVote !== true) &&
    // //         ((this.props.state.voteState === OperationState.NotStarted) || (this.props.state.voteState === OperationState.Failed))) {
    // //         Actions.setDialog(Dialog.vote);
    // //         Actions.setRapView(RapView.describe);
    // //     }
    // // }

    let joinThisDiscussionButtonText: string;
    switch (post?.type) {
        case CommentType.Issue:
            joinThisDiscussionButtonText = ResourceStrings.iHaveThisProblemToo();
            break;
        case CommentType.Suggestion:
            joinThisDiscussionButtonText = ResourceStrings.iHaveThisSuggestionToo();
            break;
        default:
            break;
    }

    const showOnJoinDiscussion = onJoinDiscussion && joinThisDiscussionButtonText && !post.cantVote && !post.hasVoted;

    const menuProps = (post: RootPost): IContextualMenuProps => ({
        items: [
            {
                key: 'JustVote',
                text: ResourceStrings.justVote(),
                iconProps: { iconName: 'AddFriend' },
                onClick: function () {
                    App.telemetry.deflect(deflectInfo, "suggestion/vote_instant");
                    onUpvote(post);
                }
            },
            {
                key: 'EnhancedVote',
                text: ResourceStrings.voteWithMoreInfo(),
                iconProps: { iconName: 'Feedback' },
                onClick: function () {
                    App.telemetry.deflect(deflectInfo, "suggestion/vote_and_provide_info");
                    onJoinDiscussion(post);
                }
            },
        ],
    });

    return <PostDetailWrapper>
        {!post && <div>{"We were unable to get this feedback item. It could be because you don't have access to it or it doesn't exist."}</div>}

        {post && <>
            <div>
                <a
                    css="display: inline-flex; align-items: center"
                    href={App.getTicketUrl(post.answerHubId, post.title, true, true, userId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Go to details and discussion"
                    onClick={(evt) => {
                        App.telemetry.deflect(deflectInfo, "suggestion/open_details");
                        evt.stopPropagation();
                    }}
                >
                    <span>Go to details and discussion&nbsp;</span>
                    <Icon iconName="OpenInNewTab" />
                </a>
            </div>

            <TopBar>
                <State stateKey={post.state} type={post.type === CommentType.Issue ? PostType.problem : PostType.suggestion} />
                <ReportDate>Reported {reportDate}</ReportDate>
                <Author href={post.createdByUrl} target="_blank" rel="noopener noreferrer">{post.createdBy}</Author>
            </TopBar>

            {(post.cantVote || post.hasVoted) && <div className="message-light">{getVoteTooltipText(post, signInState)}</div>}
            {postV3?.data && <HiddenTextInfo post={postV3.data} currentUserId={postV3.data.currentUserId} />}
            <Description>
                <HtmlPreview text={post.text} isPublic={post.spaceId !== SpaceId.Internal} />
            </Description>

            <VotesTagsBar showVotesOrJoinDicussionSpan={showOnJoinDiscussion || !hideVotes}>
                {(showOnJoinDiscussion || !hideVotes) && <VoteButtonWrapper showOnJoinDiscussion={showOnJoinDiscussion}>
                    {!hideVotes && <Votes>
                        <span>{post.totalVotes}</span>
                        <span className="vote-label">{ResourceStrings.votes()}</span>
                        {/* @TODO: Need to discus upvotingand then implement */}
                        {/* {!post.cantVote && !post.hasVoted && onUpvote && <Icon
                    iconName="SkypeCircleArrow"
                    onClick={() => onUpvote(post)} />} */}
                    </Votes>}

                    {showOnJoinDiscussion && postV3?.data && <DefaultButton
                        text={joinThisDiscussionButtonText}
                        data-cy="rap-join-discussion-btn"
                        disabled={post.cantComment}
                        primary
                        menuProps={menuProps(postV3?.data)} />}

                </VoteButtonWrapper>}

                <TagsContainer alignLeft={!showOnJoinDiscussion && hideVotes}>
                    {post.tags.map((tag, i) => <Tag key={i}>{tag.value}</Tag>)}
                </TagsContainer>
            </VotesTagsBar>

            {!!postV3?.data?.solutionCount && (
                <DetailsWrapper
                    title={(
                        <HeaderPrimaryItem className="solutions-accordion-header">
                            <Icon iconName="SkypeCircleCheck" />
                            <span>{post.solutionCount} {ResourceStrings.answers()}</span>
                        </HeaderPrimaryItem>
                    )}
                    onExpandedToggle={isExpanded => {
                        if (isExpanded) {
                            App.telemetry.deflect(deflectInfo, "suggestion/open_solutions");
                        }
                    }}>
                    <Solutions ticket={postV3.data} />
                </DetailsWrapper>
            )}
            {!!postV3?.data?.commentCount && (
                <DetailsWrapper
                    title={(
                        <HeaderPrimaryItem className="comments-accordion-header">
                            <Icon iconName="MessageFill" />
                            <span>{postV3.data.commentCount} {ResourceStrings.comments()}</span>
                        </HeaderPrimaryItem>
                    )}
                    onExpandedToggle={isExpanded => {
                        if (isExpanded) {
                            App.telemetry.deflect(deflectInfo, "suggestion/open_comments");
                        }
                    }}
                    >
                    <Comments ticket={postV3.data} />
                </DetailsWrapper>
            )}
        </>}
    </PostDetailWrapper>
}

export const State = ({ stateKey, type }: { stateKey: string; type: PostType }): JSX.Element =>
    <StateWrapper className={`post-state ${stateKey}`}>{App.LocalizeStateName(stateKey, type)}</StateWrapper>;

const StateWrapper = styled.span`
    border-radius: .2rem;
    padding: .4rem .8rem;
    text-transform: capitalize;
`;

const PostDetailWrapper = styled.div`
    display: grid;
    grid-template-columns: 100%;
    grid-gap: 1.5rem;

    margin: 2rem 1.5rem 0 1rem;
    padding-bottom: 1.2rem;

    button {
        white-space: nowrap;
    }

    .message-light {
        color: var(--color-on-surface-light);
    }

    ${forMinWidth.sm`
        margin: 2rem 2.2rem 0 2.8rem;
    `}

    ${forMinWidth.md`
        margin: 2rem 2.8rem 0 3.8rem;
    `}

    ${forMinWidth.lg`
        margin: 2rem 2.8rem 0 4.8rem;
    `}
`;

const VoteButtonWrapper = styled.span<{ showOnJoinDiscussion: boolean }>`
    display: grid;
    grid-template-columns: max-content;
    grid-gap: 1rem;

    ${forMinWidth.sm`
        grid-template-columns: ${props => props.showOnJoinDiscussion ? "repeat(2, max-content)" : "max-content"};

        > span {
            grid-column: ${props => props.showOnJoinDiscussion ? 2 : 1};
            grid-row: 1;
        }
    `}
`;

const Description = styled.div`
    word-break: break-word;

    pre, code {
            overflow-wrap: break-word;
            white-space: pre-wrap;
    }
`;

const DetailsWrapper = styled(Accordion)`
    && {
        display: block;
        background-color: var(--color-background);
    }

    .solutions-accordion-header, .comments-accordion-header {
        >* + * {
            margin-left: .8rem;
        }
    }

    .solutions-accordion-header {
        color: var(--color-success);
    }

    .comments-accordion-header {
        color: var(--color-on-background-lighter);
    }

    pre, pre code {
        overflow-wrap: break-word;
        white-space: pre-wrap;
    }
`;

const Tag = styled.span`
    font-size: 1.4rem;
    color: var(--color-on-surface);
    white-space: nowrap;
`;

const TagsContainer = styled.span<{ alignLeft?: boolean }>`
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    flex-grow: 1;

    justify-content: flex-start;
    margin-left: 0;

    >:not(:last-child)::after {
        content: '|';
        padding: 0 0.8rem;
    }

    ${forMinWidth.lg`
        justify-content: ${props => props.alignLeft ? 'flex-start' : 'flex-end'};
    `}
`;

const Votes = styled.span`
    display: flex;
    font-size: 1.8rem;
    font-weight: bold;
    color: var(--color-on-surface);

    i {
        margin-left: 0.5rem;
        transform: rotate(90deg);
        transform-origin: center;
        cursor: pointer;
    }

    .vote-label {
        padding-left: .5rem;
        font-size: 1.6rem;
        font-weight: normal;
    }
`;

const ReportDate = styled.span`
    font-size: 1.8rem;
    &::after {
        content: '|';
        padding-left: 1.5rem;
    }
`;

const Author = styled.a`
    font-size: 1.8rem;
    color: var(--color-on-surface-link);
`;

const TopBar = styled.div`
    && {
        display: grid;
        grid-template-columns: max-content;
        grid-gap: .4rem;

        ${forMinWidth.sm`
            grid-template-columns: repeat(3, max-content);
            grid-gap: 1rem;
        `}
    }
`;

const VotesTagsBar = styled.div<{ showVotesOrJoinDicussionSpan: boolean }>`
    display: grid;
    grid-template-columns: 100%;
    grid-gap: .5rem;

    ${forMinWidth.lg`
        grid-template-columns: ${props => props.showVotesOrJoinDicussionSpan ? 'max-content 1fr' : '1fr'};
        grid-gap: 2rem;
    `}
`;
